import { Helmet } from 'react-helmet-async';
import { Container, Stack, Typography } from '@mui/material';
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { Course } from "../api/course-service";
import CourseService, { coverUrl } from "../api/course-service";
import { CatalogCard } from '../components/catalog/CatalogCard';
import { CatalogGroup } from '../components/catalog/CatalogGroup';
import ENV from "../env";
import { CatalogHeader } from "../components/catalog/CatalogHeader";
import { CatalogFilter } from "../components/catalog/CatalogFilter";
import { groupByTitle } from "../utils/grouping";
import { byTitle } from "../utils/by-title";

// A course with fewer questions than this cannot fill a workout of every lesson, so it is
// still advertised as a preview.
const PREVIEW_QUESTIONS = 100;

export default function CoursesPage() {
    const navigate = useNavigate();

    const [ courses, setCourses ] = useState<Course[]>([]);
    const [ loading, setLoading ] = useState(true);

    const [ search, setSearch ] = useState('');
    const [ group, setGroup ] = useState<string | undefined>(undefined);

    // The backend keeps the courses of one group together, so reading them in order rebuilds the groups
    const allGroups = groupByTitle(courses, (course) => course.group);
    const query = search.trim().toLowerCase();
    const groups = allGroups
        .filter((candidate) => !group || candidate.title === group)
        .map((candidate) => ({ ...candidate, items: candidate.items.filter((course) => course.title.toLowerCase().includes(query)).sort(byTitle) }))
        .filter((candidate) => candidate.items.length);

    useEffect(() => {
        CourseService.courses()
            .then((loaded) => setCourses(loaded.filter((course) => course.questions > 0)))
            .catch((reason) => console.error("Unable to load courses: ", reason))
            .finally(() => setLoading(false));
    }, []);

    return <>
        <Helmet>
            <title>Courses | { ENV.APP_NAME }</title>
        </Helmet>

        <Container maxWidth="xl">
            <Stack spacing={ 4 }>
                <CatalogHeader title="Courses"
                               subtitle="Whole subjects, lesson by lesson: read the material, verify it, recap it a week later."
                               search={ { label: 'Search courses', placeholder: 'Java, Docker, React…', value: search, onChange: setSearch } }/>

                <CatalogFilter groups={ allGroups.map((candidate) => candidate.title) } selected={ group }
                               allLabel="All courses" onSelect={ setGroup }/>

                { !loading && !courses.length && (
                    <Typography>No courses available right now.</Typography>
                ) }

                {
                    groups.map((courseGroup) => (
                        <CatalogGroup key={ courseGroup.title } title={ courseGroup.title }
                                      count={ `${ courseGroup.items.length } ${ courseGroup.items.length === 1 ? 'course' : 'courses' }` }>
                            {
                                courseGroup.items.map((course) => (
                                    <CatalogCard key={ course.id }
                                                 item={ {
                                                     cover: coverUrl(course),
                                                     title: course.title,
                                                     size: course.lessons.length,
                                                     counter: 'lessons',
                                                     preview: course.questions < PREVIEW_QUESTIONS
                                                 } }
                                                 onClick={ () => navigate(`/courses/${ course.id }`) }/>
                                ))
                            }
                        </CatalogGroup>
                    ))
                }
            </Stack>
        </Container>
    </>;
}
